// Keep your FINDING_COLORS somewhere importable
const FINDING_COLORS = {
  lungs: "rgba(58, 57, 57, 0.6)",
  red: "rgba(160, 0, 0, 1)",
  green: "rgba(0, 180, 0, 1)",
  blue: "rgba(0, 0, 200, 1)",
  cyan: "rgba(0, 180, 180, 1)",
  pink: "rgba(200, 100, 100, 1)",
  brown: "rgba(110, 110, 0, 1)",
  bordoux: "rgba(120, 0, 50, 1)",
  purple: "rgba(170, 30, 170, 1)",
  orange: "rgba(180, 125, 0, 1.0)",
};

/**
 * Pure overlay painter. No React state, no refs. TEMP: returns centers of annotation for the annotation markers (TODO: remove when genesis algo will sopurt it)
 * @param {Object} p
 * @param {any} p.viewport - Cornerstone viewport
 * @param {number} p.sliceIndex - 0-based slice index
 * @param {Object} p.meta - rows, columns, pixelSpacing, imagePositionPatient, imageOrientationPatient
 * @param {Object} p.examData - { jsonData: { slices, spots, nodules, grains, ... } }
 * @param {HTMLCanvasElement} p.canvas - target canvas to draw on
 * @param {boolean} [p.showProbeOnly=false] - if true, only draw orange spots
 */
export function drawOverlayPure({
  viewport,
  sliceIndex,
  meta,
  examData,
  canvas,
  showProbeOnly = false,
}) {
  if (!meta || !examData?.jsonData || !canvas || !viewport) return;

  const ctx = canvas.getContext("2d");
  const vpCanvas = viewport.canvas;

  // Match target canvas to the viewport’s rendered size
  const rect = vpCanvas.getBoundingClientRect();
  canvas.width = Math.max(1, Math.round(rect.width));
  canvas.height = Math.max(1, Math.round(rect.height));
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const centers = [];

  const {
    rows,
    columns,
    pixelSpacing,
    imagePositionPatient,
    imageOrientationPatient,
  } = meta;

  const sliceNum = sliceIndex + 1;
  const slice = examData.jsonData.slices.find((s) => s.sliceNum === sliceNum);
  if (!slice) return;

  // --- MASK ---
  if (slice.lungs?.indices?.length) {
    // cache per-call (simple)
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = columns;
    maskCanvas.height = rows;
    const maskCtx = maskCanvas.getContext("2d");
    const maskImage = maskCtx.createImageData(columns, rows);
    for (let i = 0; i < slice.lungs.indices.length; ++i) {
      const idx = slice.lungs.indices[i];
      maskImage.data[idx * 4 + 3] = 255; // alpha
    }
    maskCtx.putImageData(maskImage, 0, 0);

    ctx.save();
    ctx.globalAlpha = 1.0;
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const tl = viewport.worldToCanvas(imagePositionPatient);
    const tr = viewport.worldToCanvas([
      imagePositionPatient[0] +
        columns * pixelSpacing[1] * imageOrientationPatient[3],
      imagePositionPatient[1] +
        columns * pixelSpacing[1] * imageOrientationPatient[4],
      imagePositionPatient[2],
    ]);
    const bl = viewport.worldToCanvas([
      imagePositionPatient[0] +
        rows * pixelSpacing[0] * imageOrientationPatient[0],
      imagePositionPatient[1] +
        rows * pixelSpacing[0] * imageOrientationPatient[1],
      imagePositionPatient[2],
    ]);

    const dx = tr[0] - tl[0];
    const dy = tr[1] - tl[1];
    const dx2 = bl[0] - tl[0];
    const dy2 = bl[1] - tl[1];

    ctx.globalCompositeOperation = "destination-out";
    ctx.imageSmoothingEnabled = false;
    ctx.setTransform(
      dx / columns,
      dy / columns,
      dx2 / rows,
      dy2 / rows,
      tl[0],
      tl[1]
    );
    ctx.drawImage(maskCanvas, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = "source-over";
    ctx.restore();
  }

  // --- SPOTS ---
  if (slice.spots) {
    Object.entries(slice.spots).forEach(([colorName, spotIds]) => {
      if (showProbeOnly && colorName !== "orange") return;
      const color = FINDING_COLORS[colorName] || "white";
      ctx.fillStyle = color;
      const ids = Array.isArray(spotIds) ? spotIds : [spotIds];
      ids.forEach((spotId) => {
        const spot = examData.jsonData.spots.find((s) => s.id === spotId);
        if (!spot?.indices) return;
        const indices = Array.isArray(spot.indices)
          ? spot.indices
          : [spot.indices];
        indices.forEach((i) => {
          const col = i % columns;
          const row = Math.floor(i / columns);
          const [cx, cy] = viewport.worldToCanvas([
            imagePositionPatient[0] +
              col * pixelSpacing[1] * imageOrientationPatient[3] +
              row * pixelSpacing[0] * imageOrientationPatient[0],
            imagePositionPatient[1] +
              col * pixelSpacing[1] * imageOrientationPatient[4] +
              row * pixelSpacing[0] * imageOrientationPatient[1],
            imagePositionPatient[2],
          ]);
          ctx.beginPath();
          ctx.arc(cx, cy, 1.1, 0, 2 * Math.PI);
          ctx.fill();
        });
      });
    });
  }

  // --- NODULE OUTLINES (orange circle on main slice) ---
  (examData.jsonData.nodules || []).forEach((nodule) => {
    const grain = examData.jsonData.grains.find(
      (g) => g.id === nodule.grain_id
    );
    if (!grain || grain.main_slice - 1 !== sliceIndex) return;

    const spotIds = Array.isArray(grain.spots) ? grain.spots : [grain.spots];
    const coords = spotIds
      .flatMap(
        (id) => examData.jsonData.spots.find((s) => s.id === id)?.indices || []
      )
      .map((i) => {
        const col = i % columns;
        const row = Math.floor(i / columns);
        return viewport.worldToCanvas([
          imagePositionPatient[0] +
            col * pixelSpacing[1] * imageOrientationPatient[3] +
            row * pixelSpacing[0] * imageOrientationPatient[0],
          imagePositionPatient[1] +
            col * pixelSpacing[1] * imageOrientationPatient[4] +
            row * pixelSpacing[0] * imageOrientationPatient[1],
          imagePositionPatient[2],
        ]);
      });

    if (!coords.length) return;
    const xs = coords.map((c) => c[0]);
    const ys = coords.map((c) => c[1]);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;

    // convert to world coords so React can update finding annotations
    const [worldX, worldY] = viewport.canvasToWorld([cx, cy, 0]);
    centers.push({ originalId: nodule.id, x: worldX, y: worldY });

    const distances = coords.map(([x, y]) => Math.hypot(x - cx, y - cy));
    const radius = Math.max(...distances) + 5;
    ctx.save();
    ctx.strokeStyle = FINDING_COLORS["orange"];
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.restore();
  });
  return { centers };
}
